import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { InfoScreenData } from '../types/InfoScreenData';
import { Location } from '../types/Location';

import { environment } from '@environments/environment';
import { InfoscreenDummyDataStore } from './infoscreen-data.store';
import { BookingTypeEnum } from 'app/types/bookingType';
import { ConfigService } from '../services/config.service';

@Injectable({
  providedIn: 'root',
})
export class InfoscreenService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private dummyStore = inject(InfoscreenDummyDataStore);

  private generateRandomText(
    prefix: string,
    suffix: string = '',
    minLength: number,
    maxLength: number,
    wordCount?: number
  ): string {
    const words = [
      'Lorem',
      'ipsum',
      'dolor',
      'sit',
      'amet',
      'consectetur',
      'adipiscing',
      'elit',
      'sed',
      'do',
      'eiusmod',
      'tempor',
      'incididunt',
      'ut',
      'labore',
      'et',
      'dolore',
      'magna',
      'aliqua',
      'enim',
      'ad',
      'minim',
      'veniam',
      'quis',
      'nostrud',
      'exercitation',
      'ullamco',
      'laboris',
      'nisi',
      'aliquip',
      'ex',
      'ea',
      'commodo',
      'consequat',
    ];

    let text = prefix;

    // If wordCount is specified, generate exact number of words
    if (wordCount !== undefined) {
      const wordsToAdd: string[] = [];
      for (let i = 0; i < wordCount; i++) {
        wordsToAdd.push(words[Math.floor(Math.random() * words.length)]);
      }
      text += (prefix ? ' ' : '') + wordsToAdd.join(' ');
      if (suffix) {
        text += ' ' + suffix;
      }
      return text;
    }

    const targetLength =
      Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;

    while (text.length < targetLength - suffix.length) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      text += (text === prefix ? '' : ' ') + randomWord;
    }

    return (text + (suffix ? ' ' + suffix : '')).substring(0, maxLength);
  }

  generateDummyInfoScreenDatas(count: number = 24): InfoScreenData[] {
    const now = new Date();

    // Calculate next starting hour
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);

    const elements: InfoScreenData[] = [];

    const resourceParentNames = ['Hal 1', 'Hal 2', 'Hal 3'];
    const resourceNames = ['Bane 1', 'Bane 2', 'Bane 3'];

    // Pick a random index for the BLOCKED booking
    const blockedIndex = Math.floor(Math.random() * count);

    for (let i = 0; i < count; i++) {
      // Create activity starting from next hour, incrementing by 1 hour each iteration
      const start = new Date(nextHour.getTime() + i * 60 * 60 * 1000);
      const end = new Date(start.getTime() + 60 * 60 * 1000); // 1 hour duration

      const randomResourceParentName =
        resourceParentNames[
          Math.floor(Math.random() * resourceParentNames.length)
        ];

      const randomResourceName =
        resourceNames[Math.floor(Math.random() * resourceNames.length)];

      // BookingType BLOCKED for the randomly chosen index
      if (i === blockedIndex) {
        elements.push({
          id: i + 1,
          activity: 'Spærring',
          resource: randomResourceName,
          resourceParent: randomResourceParentName,
          start: start.toISOString(),
          end: end.toISOString(),
          title: 'Spærring: Andet',
          bookingType: BookingTypeEnum[BookingTypeEnum.BLOCKED],
          skillLevel: '',
          lockerRooms: [],
        });
      } else {
        elements.push({
          id: i + 1,
          activity: this.dummyStore.getRandomSportActivity(),
          resource: randomResourceName,
          resourceParent: randomResourceParentName,
          start: start.toISOString(),
          end: end.toISOString(),
          title: this.generateRandomText('Infoskærm ', 'tekst', 20, 100),
          bookingType: BookingTypeEnum[BookingTypeEnum.BOOKING],
          skillLevel: this.dummyStore.getRandomSkillLevel(),
          lockerRooms: [
            {
              id: 1,
              bookingPeriodId: 101,
              lockerRoomResourceId: 201,
              lockerRoomResourceName: this.dummyStore.getRandomLockerRoomName(),
              start: start.toISOString(),
              end: end.toISOString(),
              displayText: this.dummyStore.getRandomDanishCityTeam(),
            },
            {
              id: 2,
              bookingPeriodId: 102,
              lockerRoomResourceId: 202,
              lockerRoomResourceName: this.dummyStore.getRandomLockerRoomName(),
              start: start.toISOString(),
              end: end.toISOString(),
              displayText: this.dummyStore.getRandomDanishCityTeam(),
            },
          ],
        });
      }
    }

    return elements;
  }

  /** The location's bookings: today, or from–to (local dates and times, e.g. a week for the school timetable). */
  get(locationId: number, from?: string, to?: string): Observable<InfoScreenData[]> {
    if (this.configService.isUsingMockApi()) {
      const dummyData = this.generateDummyInfoScreenDatas();
      return of(dummyData);
    }

    const apiUrl = this.configService.getApiUrl();
    const range = from && to ? `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : '';
    return this.http
      .get<InfoScreenData[]>(`${apiUrl}/infoscreen/?locationId=${locationId}${range}`)
      .pipe(map((data: InfoScreenData[]) => data));
  }

  getLocations(): Observable<Location[]> {
    const apiUrl = this.configService.getApiUrl();
    return this.http.get<Location[]>(`${apiUrl}/locations`);
  }
}
